import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Download, Search, ChevronDown, ChevronUp, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const token = () => localStorage.getItem("token");
const cfg = () => ({ headers: { Authorization: `Bearer ${token()}` } });
const fmtIDR = (n) => (n || 0).toLocaleString("id-ID");

const SOURCE_OPTIONS = [
  { value: "invoice_c", label: "Invoice C", color: "bg-green-100 text-green-700" },
  { value: "cargo", label: "Cargo", color: "bg-blue-100 text-blue-700" },
  { value: "dn", label: "Debit Note", color: "bg-yellow-100 text-yellow-700" },
  { value: "inv_e", label: "Invoice E", color: "bg-purple-100 text-purple-700" },
  { value: "inv_a", label: "Invoice A", color: "bg-orange-100 text-orange-700" },
  { value: "storage", label: "Storage", color: "bg-gray-100 text-gray-700" },
];

const getSourceBadge = (src) => {
  const s = SOURCE_OPTIONS.find((o) => o.value === src);
  if (!s) return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">{src}</span>;
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>;
};

const BillingReportPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("summary");
  const [filters, setFilters] = useState({ date_from: "", date_to: "", customer: "", source: "", status: "ACTIVE", search: "" });
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.customer) p.customer = filters.customer;
    if (filters.source) p.source = filters.source;
    if (filters.status) p.status = filters.status;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/billing/summary`, { ...cfg(), params: buildParams() });
      setSummary(res.data);
    } catch (e) { toast.error("Gagal memuat summary"); }
    finally { setLoading(false); }
  }, [buildParams]);

  const fetchLedger = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/billing/ledger`, { ...cfg(), params: { ...buildParams(), page: p, per_page: 50 } });
      setLedger(res.data);
      setPage(p);
    } catch (e) { toast.error("Gagal memuat data"); }
    finally { setLoading(false); }
  }, [buildParams]);

  const fetchCustomerDetail = async (code, name) => {
    const key = code || name;
    if (expandedCustomer === key) { setExpandedCustomer(null); return; }
    setExpandedCustomer(key);
    if (customerDetail[key]) return;
    try {
      const p = { ...buildParams(), customer: code || name, per_page: 200 };
      const res = await axios.get(`${API_URL}/billing/ledger`, { ...cfg(), params: p });
      setCustomerDetail((prev) => ({ ...prev, [key]: res.data.items }));
    } catch (e) { toast.error("Gagal memuat detail"); }
  };

  useEffect(() => {
    if (view === "summary") fetchSummary();
    else fetchLedger(1);
  }, [view, fetchSummary, fetchLedger]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/billing/sync`, {}, cfg());
      toast.success("Sync selesai!");
      if (view === "summary") fetchSummary(); else fetchLedger(page);
    } catch (e) { toast.error("Sync gagal: " + (e.response?.data?.detail || e.message)); }
    finally { setSyncing(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/billing/export`, { ...cfg(), params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `billing_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export gagal"); }
  };

  const applyFilters = () => {
    if (view === "summary") fetchSummary(); else fetchLedger(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <div className="flex justify-between items-center">
        <button onClick={() => navigate("/portal")} className="flex items-center gap-2 px-4 py-2 bg-white text-slate-600 hover:text-blue-600 hover:bg-blue-50 font-bold rounded-xl shadow-sm border border-slate-200 transition">
          <ArrowLeft size={18} /> Portal
        </button>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow transition disabled:opacity-50">
            <RefreshCw size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Now"}
          </button>
          <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow transition">
            <Download size={16} /> Export XLSX
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Report</h1>
            <p className="text-gray-500 text-sm mt-1">Data terpadu dari semua sistem invoice</p>
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl hover:bg-gray-50 font-semibold">
            <Filter size={16} /> Filter {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-4 bg-gray-50 rounded-xl">
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Dari</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Sampai</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Customer</label>
              <input type="text" placeholder="Nama / Kode" value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Sumber</label>
              <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className="w-full p-2 border rounded-lg text-sm">
                <option value="">Semua</option>
                {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Cari AWB/No</label>
              <input type="text" placeholder="AWB / Doc No" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 block mb-1">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full p-2 border rounded-lg text-sm">
                <option value="ALL">Semua</option>
                <option value="ACTIVE">Active</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={applyFilters} className="w-full px-4 py-2 bg-gray-900 text-white font-bold rounded-lg hover:bg-black transition flex items-center justify-center gap-2">
                <Search size={16} /> Terapkan
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <button onClick={() => setView("summary")} className={`px-4 py-2 rounded-xl font-bold text-sm transition ${view === "summary" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            Ringkasan Customer
          </button>
          <button onClick={() => setView("detail")} className={`px-4 py-2 rounded-xl font-bold text-sm transition ${view === "detail" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            Detail Dokumen
          </button>
        </div>

        {loading && <div className="text-center py-8 text-gray-400">Memuat data...</div>}

        {!loading && view === "summary" && summary && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-black text-blue-700">{summary.total_customers}</div>
                <div className="text-xs text-blue-500 font-bold">Customer</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-black text-green-700">{fmtIDR(summary.grand_docs)}</div>
                <div className="text-xs text-green-500 font-bold">Dokumen</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-black text-orange-700">Rp {fmtIDR(summary.grand_total)}</div>
                <div className="text-xs text-orange-500 font-bold">Total Billing</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold text-gray-600">Customer</th>
                    <th className="text-center px-4 py-3 font-bold text-gray-600">Kode</th>
                    <th className="text-center px-4 py-3 font-bold text-gray-600">Docs</th>
                    <th className="text-right px-4 py-3 font-bold text-gray-600">Total (IDR)</th>
                    <th className="text-center px-4 py-3 font-bold text-gray-600">Periode</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {summary.items.map((c, i) => {
                    const key = c.customer_code || c.customer_name;
                    const isExpanded = expandedCustomer === key;
                    return (
                      <React.Fragment key={i}>
                        <tr className="hover:bg-gray-50 cursor-pointer transition" onClick={() => fetchCustomerDetail(c.customer_code, c.customer_name)}>
                          <td className="px-4 py-3 font-semibold text-gray-900 flex items-center gap-2">
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {c.customer_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-center"><span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{c.customer_code}</span></td>
                          <td className="px-4 py-3 text-center font-bold">{c.total_docs}</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">Rp {fmtIDR(c.total_amount)}</td>
                          <td className="px-4 py-3 text-center text-xs text-gray-500">{c.earliest} ~ {c.latest}</td>
                        </tr>
                        {isExpanded && customerDetail[key] && (
                          <tr>
                            <td colSpan={5} className="bg-blue-50/50 px-6 py-3">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="text-left py-1">Sumber</th>
                                    <th className="text-left py-1">No Dokumen</th>
                                    <th className="text-left py-1">Tanggal</th>
                                    <th className="text-left py-1">AWB</th>
                                    <th className="text-right py-1">Net Total</th>
                                    <th className="text-center py-1">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerDetail[key].map((d, j) => (
                                    <tr key={j} className={`border-t border-blue-100 ${d.status === "CANCELLED" ? "opacity-50 line-through" : ""}`}>
                                      <td className="py-1">{getSourceBadge(d.source_system)}</td>
                                      <td className="py-1 font-mono">{d.doc_no}</td>
                                      <td className="py-1">{d.doc_date}</td>
                                      <td className="py-1">{d.awb_no || "-"}</td>
                                      <td className="py-1 text-right font-semibold">Rp {fmtIDR(d.net_total)}</td>
                                      <td className="py-1 text-center">{d.status === "CANCELLED" ? <span className="text-red-500 text-[10px]">CANCEL</span> : <span className="text-green-500 text-[10px]">ACTIVE</span>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && view === "detail" && ledger && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-3 font-bold text-gray-600">Sumber</th>
                    <th className="text-left px-3 py-3 font-bold text-gray-600">No Dokumen</th>
                    <th className="text-left px-3 py-3 font-bold text-gray-600">Tanggal</th>
                    <th className="text-left px-3 py-3 font-bold text-gray-600">Customer</th>
                    <th className="text-left px-3 py-3 font-bold text-gray-600">AWB</th>
                    <th className="text-right px-3 py-3 font-bold text-gray-600">Net Total</th>
                    <th className="text-center px-3 py-3 font-bold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ledger.items.map((r) => (
                    <tr key={r.id} className={`hover:bg-gray-50 transition ${r.status === "CANCELLED" ? "opacity-50" : ""}`}>
                      <td className="px-3 py-2">{getSourceBadge(r.source_system)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.doc_no}</td>
                      <td className="px-3 py-2 text-xs">{r.doc_date}</td>
                      <td className="px-3 py-2 text-xs"><span className="font-semibold">{r.customer_name}</span> <span className="text-gray-400">{r.customer_code}</span></td>
                      <td className="px-3 py-2 text-xs font-mono">{r.awb_no || "-"}</td>
                      <td className="px-3 py-2 text-right font-bold">Rp {fmtIDR(r.net_total)}</td>
                      <td className="px-3 py-2 text-center">{r.status === "CANCELLED" ? <span className="bg-red-100 text-red-600 text-[10px] px-2 py-0.5 rounded-full font-bold">CANCEL</span> : <span className="bg-green-100 text-green-600 text-[10px] px-2 py-0.5 rounded-full font-bold">ACTIVE</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {ledger.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button disabled={page <= 1} onClick={() => fetchLedger(page - 1)} className="px-3 py-1.5 border rounded-lg text-sm font-bold disabled:opacity-30 hover:bg-gray-50">← Prev</button>
                <span className="px-3 py-1.5 text-sm text-gray-500">Hal {page} / {ledger.total_pages} ({ledger.total} data)</span>
                <button disabled={page >= ledger.total_pages} onClick={() => fetchLedger(page + 1)} className="px-3 py-1.5 border rounded-lg text-sm font-bold disabled:opacity-30 hover:bg-gray-50">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingReportPage;
